/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Sparkles } from 'lucide-react';

export const StartForm = ({ onSubmit, loading }) => {
  const [complaint, setComplaint] = useState('');
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [conditions, setConditions] = useState('');
  const [medications, setMedications] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (complaint.trim().length < 3) {
      toast.error('Descrivi almeno brevemente il sintomo');
      return;
    }
    onSubmit({
      initial_complaint: complaint.trim(),
      age: age ? parseInt(age, 10) : undefined,
      sex: sex || undefined,
      known_conditions: conditions ? conditions.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
      medications: medications ? medications.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="symptom-start-form">
      <div>
        <Label htmlFor="complaint">Cosa senti? Descrivi i sintomi</Label>
        <Textarea
          id="complaint"
          value={complaint}
          onChange={(e) => setComplaint(e.target.value)}
          placeholder="Es. Ho mal di testa forte da 3 giorni, con nausea e sensibilità alla luce…"
          rows={4}
          data-testid="symptom-complaint-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="age">Età</Label>
          <Input id="age" type="number" min="0" max="120" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Es. 42" data-testid="symptom-age-input" />
        </div>
        <div>
          <Label>Sesso</Label>
          <Select value={sex} onValueChange={setSex}>
            <SelectTrigger data-testid="symptom-sex-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="F">Femmina</SelectItem>
              <SelectItem value="M">Maschio</SelectItem>
              <SelectItem value="X">Altro / non specificato</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="conditions">Patologie note (opzionale)</Label>
        <Input id="conditions" value={conditions} onChange={(e) => setConditions(e.target.value)} placeholder="Ipertensione, diabete, ..." data-testid="symptom-conditions-input" />
      </div>
      <div>
        <Label htmlFor="medications">Farmaci in uso (opzionale)</Label>
        <Input id="medications" value={medications} onChange={(e) => setMedications(e.target.value)} placeholder="Ramipril 5mg, Metformina 850mg, ..." data-testid="symptom-medications-input" />
      </div>
      <Button type="submit" disabled={loading} className="w-full" data-testid="symptom-start-submit">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analisi in corso…</> : <><Sparkles className="h-4 w-4" /> Avvia analisi AI</>}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center">
        Le informazioni non sostituiscono il parere medico. In caso di emergenza chiama il 118.
      </p>
    </form>
  );
};
